import type { Cell } from "./types";
import { state } from "./state";
import { LEVELS } from "./levels";
import { key } from "./geometry";
import { starsFor } from "./scoring";
import { $ } from "../dom";
import {
  applyCoverage, buildGrid, buildTrees, layout as layoutBoard, renderNorias,
} from "./board";
import { clearPreview } from "./interaction";
import { openReveal, prepareSolves, resetReveal } from "../reveal/reveal";

/* ---------- Level load ---------- */
export function loadLevel(i: number): void {
  state.idx = i;
  const L = state.level();
  state.treeSet = new Set(L.trees.map(([r, c]) => key(r, c)));
  state.rockSet = new Set(L.rocks.map(([r, c]) => key(r, c)));
  // restore the player's saved placement for this level (so navigating back keeps work)
  state.norias = state.saved[i].map(([r, c]) => ({ id: state.nextId++, r, c }));
  state.aliveSet = new Set();
  state.drag = null;
  clearPreview();
  buildGrid();
  buildTrees();
  layoutBoard();
  recompute();
  updateNav();
}

export function layout(): void {
  layoutBoard();
  recompute();
}

/* ---------- Recompute (render + HUD + stars + persistence) ---------- */
export function recompute(): void {
  const L = state.level();
  renderNorias();
  const watered = applyCoverage();

  // persist the current placement + record for the reveal
  state.saved[state.idx] = state.norias.map((n): [number, number] => [n.r, n.c]);
  state.records[state.idx] = {
    norias: state.norias.map((n): Cell => [n.r, n.c]),
    watered,
  };

  // HUD
  $("levelVal").textContent = (state.idx + 1) + " / " + LEVELS.length;
  $("noriaVal").textContent = String(L.norias - state.norias.length);
  $("palmVal").textContent = watered + " / " + L.total;
  $("levelName").innerHTML = "Oasis " + (state.idx + 1) + " — <b>" + L.name + "</b>";

  // stars
  const stars = starsFor(L, watered);
  const starEls = $("stars").children;
  for (let i = 0; i < 3; i++) starEls[i].classList.toggle("on", i < stars);

  // message
  const msg = $("msg");
  msg.className = "msg";
  if (watered === L.total) {
    msg.classList.add("win");
    msg.textContent = "🌿 Oasis Restored! Every palm drinks again.";
  } else if (watered >= L.s3) {
    msg.classList.add("optimal");
    msg.textContent = "✦ Optimal irrigation — you've reached as many as the wheels allow!";
  } else if (stars === 2) {
    msg.textContent = "Lush, but a grove still thirsts. Can you reposition for more?";
  } else if (stars === 1) {
    msg.textContent = "A start — many palms are still dry.";
  } else if (state.norias.length === 0) {
    msg.textContent = "Place a noria to begin.";
  } else {
    msg.textContent = "The sands stay dry here — try moving the wheels closer to the palms.";
  }

  if (stars === 3 && !state.completed[state.idx]) {
    state.completed[state.idx] = true;
    updateNav();
  }
  updateNextBtn(stars);
}

/* ---------- Nav ---------- */
export function buildDots(): void {
  const d = $("dots");
  d.innerHTML = "";
  LEVELS.forEach((_, i) => {
    const b = document.createElement("div");
    b.className = "dot";
    b.textContent = String(i + 1);
    b.addEventListener("click", () => loadLevel(i));
    d.appendChild(b);
  });
}

export function updateNav(): void {
  const dots = $("dots").children;
  for (let i = 0; i < dots.length; i++) {
    dots[i].classList.toggle("current", i === state.idx);
    dots[i].classList.toggle("done", state.completed[i]);
    dots[i].textContent = state.completed[i] ? "✓" : String(i + 1);
  }
  ($("prevBtn") as HTMLButtonElement).disabled = state.idx === 0;
  $("nextBtn").textContent = state.idx === LEVELS.length - 1 ? "See the optimizer ✦" : "Next ▸";
}

function updateNextBtn(stars: number): void {
  // glow the Next button once you've done well, to invite progress
  $("nextBtn").classList.toggle("glow", stars >= 3);
}

export function goPrev(): void {
  if (state.idx > 0) loadLevel(state.idx - 1);
}

export function goNext(): void {
  if (state.idx < LEVELS.length - 1) loadLevel(state.idx + 1);
  else openReveal();
}

export function resetLevel(): void {
  state.norias = [];
  clearPreview();
  recompute();
}

/* ---------- Run lifecycle ---------- */
export function startGame(): void {
  state.phase = "play";
  loadLevel(0);
  // download/initialise the WASM solver during play so the reveal is instant.
  // Swallow failures here (e.g. offline / blocked CDN) — the reveal re-awaits
  // each solve and surfaces a graceful "solver unreachable" message instead.
  prepareSolves().catch(() => { /* solver warm-up failed; handled at reveal time */ });
}

export function restartGame(): void {
  state.completed = LEVELS.map(() => false);
  state.saved = LEVELS.map(() => []);
  state.records = LEVELS.map(() => ({ norias: [], watered: 0 }));
  $("solverOverlay").classList.add("hidden");
  $("learnOverlay").classList.add("hidden");
  resetReveal();
  loadLevel(0);
}
